// @ts-nocheck
/* AUTO-COPIED from frontend/src/config by scripts/sync-legal.js — DO NOT EDIT. */
/**
 * legalConfig — single source of truth for company / contact / identity
 * values that appear in the Privacy Policy, Terms of Service, Advisor
 * Agreement and every legal / grievance surface.
 *
 * NOTHING about the entity should be hard-coded in a component. Every
 * value below is overridable at build time via a `REACT_APP_BU_*`
 * environment variable (Netlify dashboard / `.env`), mirroring the
 * server-side `legal-config.env` (BU_* keys) documented in
 * BU-LGL-GUIDE §0. The literals here are only the documented fallback
 * defaults so the app renders sensibly when an env var is unset.
 *
 * To change the registered name, CIN, address, emails, phone, grievance
 * officer, jurisdiction or document versions, set the matching
 * `REACT_APP_BU_*` var — no component edits required.
 */

const env = (typeof process !== 'undefined' && process.env) || {};

function pick(key: string, fallback: string): string {
  const v = env[key];
  return typeof v === 'string' && v.trim() ? v.trim() : fallback;
}

export interface LegalConfig {
  legalName: string;
  brandName: string;
  cin: string;
  gstin: string;
  pan: string;
  regOffice: string;
  city: string;
  state: string;
  country: string;
  jurisdictionCity: string;
  jurisdictionState: string;
  arbitrationSeat: string;
  phone: string;
  contactEmail: string;
  grievanceEmail: string;
  supportUrl: string;
  website: string;
  appId: string;
  grievanceOfficerName: string;
  grievanceOfficerDesignation: string;
  grievanceOfficerPhone: string;
  grievanceResponseDays: string;
  dpoName: string;
  dpoEmail: string;
  effectiveDate: string;
  privacyVersion: string;
  tosVersion: string;
  cookieVersion: string;
  advisorVersion: string;
}

export const LEGAL: LegalConfig = {
  // Company legal identity is intentionally GENERIC for now — the registered
  // entity name, address, CIN, GSTIN etc. are NOT rendered anywhere in-app and
  // will be supplied later via the REACT_APP_BU_* env vars. Defaults are safe
  // placeholders so no real/unregistered company detail is ever shown.
  legalName: pick('REACT_APP_BU_LEGAL_NAME', 'BimaUncle'),
  brandName: pick('REACT_APP_BU_BRAND_NAME', 'BimaUncle'),
  cin: pick('REACT_APP_BU_CIN', '[CIN]'),
  gstin: pick('REACT_APP_BU_GSTIN', '[GSTIN]'),
  pan: pick('REACT_APP_BU_PAN', '[PAN]'),
  regOffice: pick('REACT_APP_BU_REG_OFFICE', '[Registered Office]'),
  city: pick('REACT_APP_BU_CITY', '[City]'),
  state: pick('REACT_APP_BU_STATE', '[State]'),
  country: pick('REACT_APP_BU_COUNTRY', 'India'),
  jurisdictionCity: pick('REACT_APP_BU_JURISDICTION_CITY', '[Jurisdiction]'),
  jurisdictionState: pick('REACT_APP_BU_JURISDICTION_STATE', '[State]'),
  arbitrationSeat: pick('REACT_APP_BU_ARBITRATION_SEAT', '[Arbitration Seat]'),
  phone: pick('REACT_APP_BU_PHONE', ''),
  contactEmail: pick('REACT_APP_BU_CONTACT_EMAIL', 'hello@bimauncle.com'),
  grievanceEmail: pick('REACT_APP_BU_GRIEVANCE_EMAIL', 'grievance@bimauncle.com'),
  supportUrl: pick('REACT_APP_BU_SUPPORT_URL', 'https://bimauncle.com/support'),
  website: pick('REACT_APP_BU_WEBSITE', 'https://bimauncle.com'),
  appId: pick('REACT_APP_BU_APP_ID', 'com.bimauncle.calculator'),
  grievanceOfficerName: pick('REACT_APP_BU_GRIEVANCE_OFFICER_NAME', '[Grievance Officer Name]'),
  grievanceOfficerDesignation: pick(
    'REACT_APP_BU_GRIEVANCE_OFFICER_DESIGNATION',
    'Grievance Officer',
  ),
  grievanceOfficerPhone: pick('REACT_APP_BU_GRIEVANCE_OFFICER_PHONE', '[Grievance Officer Phone]'),
  grievanceResponseDays: pick('REACT_APP_BU_GRIEVANCE_RESPONSE_DAYS', '15'),
  dpoName: pick('REACT_APP_BU_DPO_NAME', '[Data Protection Officer]'),
  dpoEmail: pick('REACT_APP_BU_DPO_EMAIL', 'grievance@bimauncle.com'),
  effectiveDate: pick('REACT_APP_BU_EFFECTIVE_DATE', '6 July 2026'),
  privacyVersion: pick('REACT_APP_BU_PRIVACY_VERSION', 'pp-v6.1-2026-07-06'),
  tosVersion: pick('REACT_APP_BU_TOS_VERSION', 'tos-v4.0-2026-07-06'),
  cookieVersion: pick('REACT_APP_BU_COOKIE_VERSION', 'cp-v1.0-2026-07-06'),
  advisorVersion: pick('REACT_APP_BU_ADVISOR_VERSION', 'aa-v3.0-2026-06-12'),
};

/** "technology service provider — not an insurer/broker/aggregator" — the
 *  one positioning sentence reused across surfaces. */
export const POSITIONING_LINE =
  `${LEGAL.legalName} is a technology service provider. It is not an ` +
  `insurer, broker, corporate agent or aggregator.`;

/** Full landing-page footer disclaimer (single paragraph): non-insurer
 *  positioning + indicative-pricing + government/IRDAI non-affiliation +
 *  trademark notice + ©. Mirrors the Play Store listing disclaimer so the
 *  store, the in-app footer and the legal pages stay consistent. */
export const FOOTER_DISCLAIMER =
  `${LEGAL.brandName} is a technology service provider and not an insurer, broker, ` +
  `corporate agent, or web aggregator. Premium calculations are indicative and based ` +
  `on insurer-published rates; actual premiums are subject to insurer approval. ` +
  `${LEGAL.brandName} is not affiliated with or endorsed by IRDAI or any government ` +
  `authority. All trademarks and logos belong to their respective owners. ` +
  `© ${new Date().getFullYear()} ${LEGAL.brandName}. All rights reserved.`;

export default LEGAL;
